import { useState } from "react"
import { CloseSnackBar } from "event-definitions"
import { uniqueId } from "library/database/split-id.mjs"
import { snackbar } from "lib/snackbar"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { warningSnackbar } from "lib/snackbar/warning-snackbar"
import { ScenarioPromotionComplete } from "routes/scenarios/scenario-promotion-event-definitions.mjs"
import { ScenarioPromotionSnackbar } from "./scenario-promotion-snackbar"

/**
 * Manages the background scenario → draft-regime promotion UX for a scenario:
 * - notifyPromotionStarted: shows a persistent progress snackbar (updates live via progress events)
 * - on completion: closes the snackbar, surfaces the promoted regime (banner) or an error
 *
 * @param {string} scenarioId
 * @returns {{ promotedRegimeId: string|null, clearPromotedRegime: () => void,
 *             notifyPromotionStarted: (regimeName?: string) => void }}
 */
export function usePromotionNotifications(scenarioId) {
    const [promotedRegimeId, setPromotedRegimeId] = useState(null)
    ScenarioPromotionComplete.useEvent(handleComplete, [scenarioId])
    return { promotedRegimeId, clearPromotedRegime, notifyPromotionStarted }

    function notifyPromotionStarted(regimeName) {
        snackbar(<ScenarioPromotionSnackbar scenarioId={scenarioId} regimeName={regimeName} />, {
            variant: "info",
            persist: true,
            key: snackbarKeyFor(scenarioId),
        })
    }

    function handleComplete(payload) {
        if (!payload || payload.scenarioId !== scenarioId) return
        CloseSnackBar.raiseLater(snackbarKeyFor(scenarioId))
        if (payload.error) {
            errorSnackbar(`Promotion failed: ${payload.error}`)
            return
        }
        if (payload.auditWriteFailures) {
            warningSnackbar(
                `Promotion completed, but ${payload.auditWriteFailures} tailoring history ` +
                    `record(s) could not be written. The regime is intact; the affected ` +
                    `tailorings just have no audit entry.`
            )
        }
        setPromotedRegimeId(uniqueId(payload.regimeId))
    }

    function clearPromotedRegime() {
        setPromotedRegimeId(null)
    }
}

function snackbarKeyFor(scenarioId) {
    return `scenario-promotion-${scenarioId}`
}
